import { hasModule } from "csi/_base/modules";
import crews from "csi/access/crews";
import csiCard, { CardSpecs } from "csi/card/_base";
import { NameAndUrl } from "csi/comm/JsonResponse";
import PostRequest from "csi/comm/PostRequest";
import { QRY_LIMIT_PATH } from "csi/constants";
import { convertToStandardPath } from "csi/core/extendedPathService";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import ExtendedPath from "csi/core/ExtendedPath";
import { getRuntimeRulesFromNode, getRuntimeRulesQuery, getSpecForKey, getSpecsForKeys, RuleOnApplyFilterFunction, RuntimeRulesSchema } from "csi/middleware/attributeRule/RuleService";
import { ATTR_IMAGES, DEFAULT_IMAGE_PATH, getImageSrc } from "csi/model/image";
import reflection from "csi/reflection";
import { getSearch, runSearch } from "csi/service/dataStoreService";
import { resolveNode, resolveNodes, resolveNodesWithDependencies } from "csi/service/nodeService";
import Cache from "csi/state/Cache";
import rootStore, { localStore } from "csi/state/rootStore";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import queryProcessor from "csi/xml/QueryProcessor";
import Search from "csi/xml/Search";
import { ComponentItem } from "designSystem/types/component-item-types";
import _ from "lodash";
import { runInAction } from "mobx";
import PresentationPageStore from "./PresentationPageStore";
import SetEditStore from "./store/SetEditStore";

const _ruleCache = new Cache();

export const PRESENTATION_ADMIN_ROLE = "Product Presentation Administrator";
export const PRESENTATION_EDITOR_ROLE = "Product Presentation Editor";

export const PRODUCT_PRESENTATION = "ProductPresentation";
export const PRODUCT_PRESENTATION_SET = "ProductPresentationSet";
export const PRODUCT_PRESENTATION_SET_ITEM = "ProductPresentationSetItem";
export const PRODUCT_PRESENTATION_LAYOUT = "ProductPresentationLayout";

export const SET_ITEMS = "Items";
export const SET_ITEM_PRODUCT = "PPIProduct";
export const SET_ITEM_COLOR = "PPIProductColor";
export const PRESENTATION_PRODUCTS = "PPProducts";
export const PRESENTATION_RULE = "Rule";
export const PRESENTATION_SETS = "Sets";
export const PRINT_DESIGN_COLOR = "PrintDesignColor";
export const PRODUCT_COLOR_SPECIFICATION = "ProductColorSpec";
export const BASEPRODUCT_PRESENTATION_SET_ITEMS = "PPSetItems";

export const FOOD_MODULE = "FormulatedProduct";

export const FOOD = "Food";
export const MAIN_MATERIAL = "MainMaterial";
export const MATERIAL = "Material";
export const STYLE = "Style";
export const THEME = "Theme";
export const THEME_MASTER = "ThemeMaster";

export const COLORWAY = "Colorway";
export const COLORMATERIAL = "ColorMaterial";
export const COLOR_SPECIFICATION = "ColorSpecification";

/** items to exclude from the overall list of attributes */
export const EXCLUDE_ITEM_IDS = ["Comments", ATTR_IMAGES, reflection.ATTR_RECENT_CHILD_COMMENTS, reflection.ATTR_RECENT_COMMENTS, BASEPRODUCT_PRESENTATION_SET_ITEMS];

/** container url for rules */
export const PP_RULE_CONTAINER_URL = "centric://PREFERENCE/ProductPresentationRules";

/** container url for attribute setting */
export const PP_ATTRIBUTE_SETTING_CONTAINER_URL = "centric://PREFERENCE/ProductPresentationAttributeSettings";

/** polling refresh interval */
export const PDF_POLLING_INTERVAL = 1000;

/** maximum time to wait for PDFRequest to process */
export const PDF_TIMEOUT = 60000;

/** quick filter items */
export enum QUICK_FILTERS {
	ItemsAll = "RuleFilterItemsAll",
	ItemsUsedInSets = "ItemsUsedInSets",
	ItemsNotUsedInSets = "ItemsNotUsedInSets",
	ItemsWithViolations = "ItemsWithViolations",
	ItemsWithoutViolations = "ItemsWithoutViolations"
}

/** interface for the color item */
export interface ColorComponentItem extends ComponentItem {
	/** indicate if this item has violation or not */
	hasViolation?: boolean;
}

/** filter out the templates and inspiration styles */
export function excludeSpecialStyles(query: Query): Query {
	return query
		.Attr("IsTemplate", xml.OP_EQUALS, false)
		.Attr("IsInspiration", xml.OP_EQUALS, false);
}

/** 
 * test if the default color of this product is used in this presentation
 * NOTE: rely on the PPSetItem nodes, so need to make sure the nodes get cached
 */
export function defaultColorUsedInPresentation(cache: ICache, presentation: C8Node, product: C8Node): boolean {
	// if we're on the Set page, then the PPSetItems might come from other presentations, so, need to compact here
	const defaultIsUsedInSetUnderTheCurrentPP = _.compact(cache.getNodes(product.PPSetItems)).some((setItem) => {
		if (product.$URL !== setItem.PPIProduct) {
			return false;
		}

		const setUrl = setItem.__Parent__;
		const underThisPP = presentation.Sets.includes(setUrl);

		return underThisPP && xml.isEmptyURL(setItem.PPIProductColor);
	});

	return defaultIsUsedInSetUnderTheCurrentPP;
}

/** check whether or not the roles can play the editor role */
export function isPresentationEditor(): boolean {
	return crews.isInRoles([PRESENTATION_ADMIN_ROLE, PRESENTATION_EDITOR_ROLE]);
}

/** adHoc spec list from local storage */
export function getAdHocSpecs(primaryType: string, secondaryType: string): Spec[] {
	const { productPresentation } = localStore;

	const {
		adHocIdsList,
	} = productPresentation;

	const specsForPrimaryType = [...(adHocIdsList.get(primaryType) || [])].map((keyId) => getSpecForKey(new Key({ dpkey: keyId })));

	const specsForMatrixType = [...(adHocIdsList.get(secondaryType) || [])].map((keyId) => getSpecForKey(new Key({ dpkey: keyId })));
	const matrixPath = getColorMatrixPath(primaryType);

	// update the matrixExtendedPath so that we can identify them as matrix attributes
	specsForMatrixType.forEach((spec) => spec.matrixExtendedPath = matrixPath);

	// some attributes could be deleted already, so need to compact
	return _.compact([...specsForPrimaryType, ...specsForMatrixType]);
}

/**
 * return specs for keys stored in custom view; if matrix path is given, then use as prefix path to custom view keys
 * @param type 
 * @param primaryTypeOnly if true, only return specs for primary type
 * @param matrixTypeOnly if true, only return specs for the type along with the matrix path
 * @param ignoreMatrixPath if true, would ignore the matrix path, only applicable in the case of attributes configuration as admin
 * @param workingCache 
 */
export async function getAttributeRuleSpecsForType(
	type: string,
	primaryTypeOnly: boolean = false,
	matrixTypeOnly: boolean = false,
	ignoreMatrixPath: boolean = false,
	workingCache?: ICache,
): Promise<Spec[]> {
	const productRuleUrl = `${PP_RULE_CONTAINER_URL}/${type}`;
	const urls = [productRuleUrl];
	const colorMatrixPath = getColorMatrixPath(type);
	const colorRuleUrl = colorMatrixPath ? `${PP_RULE_CONTAINER_URL}/${colorMatrixPath.getEndType()}` : null;

	if (colorRuleUrl) {
		urls.push(colorRuleUrl);
	}

	const cache = workingCache || _ruleCache;
	await resolveNodes(urls, cache);

	// create product specs
	const productRuleNode = cache.get(productRuleUrl);
	const colorRuleNode = cache.get(colorRuleUrl);
	let specs: Spec[] = [];

	// it's possible that the node doesn't exist yet
	if (productRuleNode && !matrixTypeOnly) {
		specs = getSpecsFromCustomView(productRuleNode);
	}

	if (colorRuleNode && !primaryTypeOnly) {
		// combine product and color specs
		const colorSpecs = getSpecsFromCustomView(colorRuleNode, ignoreMatrixPath ? null : colorMatrixPath);
		specs = specs || [];
		return specs.concat(colorSpecs);
	}

	return specs;
}

/** return extended path for given type of product to its colors (if any) */
export function getColorMatrixPath(typeProduct: string): ExtendedPath | undefined {
	try {
		return ExtendedPath.create(typeProduct, pathCreate("ProductColors"));
	} catch (ex) {
		return;
	}
}

/** get the dependency path based on the runtime rule */
export function getDependencyPathForPresentationFromRuntimeRule(runtimeRules: RuntimeRulesSchema): string[] {
	const { ruleSpecs } = runtimeRules;
	const specs = ruleSpecs.map((ruleSpec) => ruleSpec.spec);
	const prefixPathToProduct = pathCreate(SET_ITEMS, SET_ITEM_PRODUCT);
	const paths: string[] = [];

	// collect the dependency paths based on the rules
	for (const spec of specs) {
		const { querySpec: { path } } = spec;

		if (path) {
			const standardPath = convertToStandardPath(path);
			paths.push(pathCreate(prefixPathToProduct, standardPath));;
		}
	}

	return paths;
}

/** return primary node type for presentation */
export function getPresentationColorMatrixPath(presentation: C8Node): ExtendedPath | undefined {
	return getColorMatrixPath(getPresentationPrimaryType(presentation));
}

/** return primary node type for presentation */
export function getPresentationPrimaryType(presentation: C8Node): string {
	return xml.enumGetText(presentation.ProductType);
}

/** return runtime rules for presentation */
export async function getPresentationRuntimeRules(presentationUrl: cnl, cache: ICache): Promise<RuntimeRulesSchema> {
	await resolveNodesWithDependencies([presentationUrl], [pathCreate(PRESENTATION_RULE)], cache);
	const presentation = cache.get(presentationUrl);
	const ruleNode = cache.get(presentation[PRESENTATION_RULE]);
	const colorMatrixPath = getPresentationColorMatrixPath(presentation);
	return getRuntimeRulesFromNode(ruleNode, colorMatrixPath);
}

/** return image src for product color's specification node */
export function getProductColorSpecImage(productColor: C8Node, cache: ICache): string | null {
	return getImageSrc(cache.get(productColor?.ColorSpecification));
}

/** get the valid product urls based on the query and presentation */
async function getValidProductsBasedOnQuery(presentation: C8Node, productQuery: Query): Promise<cnl[]> {
	const presentationUrl = presentation.$URL;
	const primaryType = getPresentationPrimaryType(presentation);
	const baseSearch = getSearch("BaseProduct.ProductPresentation", presentationUrl, { type: primaryType });
	baseSearch.getQuery().importQuery(productQuery);

	// return all the urls rather than the full nodes
	baseSearch.setPageLimits(1, 1, true);

	const response = await baseSearch.execute();
	const stillValidUrls = response?.getCompleteRefs();

	return stillValidUrls;
}

/** return the urls of product with violations due to color, as well as urls of color with violations */
async function getViolationOfColorBasedOnQuery(presentationUrl: cnl, colorMatrixPath: ExtendedPath, colorQuery: Query): Promise<[Set<cnl>, Set<cnl>]> {
	// construct the query
	const colorType = colorMatrixPath.getEndType();
	const baseColorSearch = new Search();
	baseColorSearch.byType(colorType);
	baseColorSearch.setLimitPath(presentationUrl, pathCreate("PPProducts", "ProductColors"));
	baseColorSearch.getQuery().importQuery(colorQuery);

	// extract the query and limit path
	const finalInvalidColorQuery = baseColorSearch.getQuery();
	const limitPath = baseColorSearch.getParameter(QRY_LIMIT_PATH);

	const request = new PostRequest(PRODUCT_PRESENTATION, "GetInvalidProductsDueToColors")
		.addParameter("PresentationUrl", presentationUrl)
		.addParameter("InvalidColorsQueryString", finalInvalidColorQuery.toString())
		.addParameter("QueryLimitPath", limitPath);

	const finalInvalidResponse = await request.go();
	const violationInfo = finalInvalidResponse.getResultNode();
	const productsOfInvalidColors: Set<cnl> = new Set(violationInfo.productsOfInvalidColors || []);
	const violationUrlsOfColor: Set<cnl> = new Set(violationInfo.violationUrlsOfColor || []);

	return [productsOfInvalidColors, violationUrlsOfColor];
}

/** 
 * get the product urls with violation(s), as well as remove the ones which are not used in any sets under this presentation
 * */
export async function processProductViolations(presentation: C8Node, cache: ICache = new WorkingCache()): Promise<void> {
	const presentationUrl = presentation.$URL;
	let finalViolationUrlsOfColor: Set<cnl> = new Set();
	const finalViolationUrlsOfProductDueToColor: Set<cnl> = new Set();
	let finalViolationUrlsOfProduct: Set<cnl> = new Set();

	try {
		// step 1, calculate the invalid products
		const ruleNode = await resolveNode(presentation[PRESENTATION_RULE], cache);
		const primaryType = getPresentationPrimaryType(presentation);
		const colorMatrixPath = getColorMatrixPath(primaryType);
		const runtimeRules = ruleNode ? getRuntimeRulesFromNode(ruleNode, colorMatrixPath) : null;

		// if no actual rule yet, then should just return
		if (!runtimeRules.ruleSpecs.length) {
			return;
		}

		const validProductQuery = getRuntimeRulesQuery(runtimeRules, null, onApplyPresentationProductFilter);
		const stillValidUrls = await getValidProductsBasedOnQuery(presentation, validProductQuery);

		// the invalid ones = all - valid
		const violationUrls = _.difference(presentation.PPProducts, stillValidUrls);

		// step2, exclude the ones which are not used in any sets if any
		if (!_.isEmpty(violationUrls)) {
			const request = new PostRequest(PRODUCT_PRESENTATION, "RemoveInvalidUnusedInSetsProductsFromPresentation")
				.addParameter("PresentationUrl", presentationUrl)
				.addParameter("InvalidProductUrls", violationUrls);

			const finalInvalidResponse = await request.go();
			finalViolationUrlsOfProduct = new Set(finalInvalidResponse?.getUrls() || []);
		}

		// step3, evaluate the colors against the rest valid products, because they could be invalid due to the colors
		if (colorMatrixPath) {
			// construct the query
			const validColorQuery = getRuntimeRulesQuery(runtimeRules, colorMatrixPath);

			const [violationUrlsOfProductsDueToColors, violationUrlsOfColor] = await getViolationOfColorBasedOnQuery(presentationUrl, colorMatrixPath, validColorQuery);
			finalViolationUrlsOfColor = violationUrlsOfColor;

			if (violationUrlsOfProductsDueToColors.size > 0) {
				for (const stillValidProductUrl of stillValidUrls) {
					if (violationUrlsOfProductsDueToColors.has(stillValidProductUrl)) {
						finalViolationUrlsOfProductDueToColor.add(stillValidProductUrl);
					}
				}
			}
		}
	} catch (ex) {
		console.error(ex);
	}

	// update the information on the banner
	const { pageStore } = rootStore;
	const ppPageStore = pageStore as PresentationPageStore;

	runInAction(() => {
		ppPageStore.setViolationUrlsOfProduct(finalViolationUrlsOfProduct);
		ppPageStore.setViolationUrlsOfProductDueToColor(finalViolationUrlsOfProductDueToColor);
		ppPageStore.setViolationColorUrls(finalViolationUrlsOfColor);
	});
}

/** predict the violations of products, product violations due to color, as well as color violations */
export async function predictProductViolations(productQuery: Query, colorQuery: Query, presentation: C8Node, cache: ICache = new WorkingCache()): Promise<void> {
	const presentationUrl = presentation.$URL;
	let finalViolationUrlsOfColor: Set<cnl> = new Set();
	const finalViolationUrlsOfProductDueToColor: Set<cnl> = new Set();
	let finalViolationUrlsOfProduct: Set<cnl> = new Set();

	try {
		// step 1, calculate the invalid products
		const primaryType = getPresentationPrimaryType(presentation);
		const stillValidUrls = await getValidProductsBasedOnQuery(presentation, productQuery);

		// the invalid ones = all - valid
		finalViolationUrlsOfProduct = new Set(_.difference(presentation.PPProducts, stillValidUrls));

		// step2, evaluate the colors against the rest valid products, because they could be invalid due to the colors
		const colorMatrixPath = getColorMatrixPath(primaryType);

		if (colorMatrixPath) {
			const [violationUrlsOfProductsDueToColors, violationUrlsOfColor] = await getViolationOfColorBasedOnQuery(presentationUrl, colorMatrixPath, colorQuery);
			finalViolationUrlsOfColor = violationUrlsOfColor;

			if (violationUrlsOfProductsDueToColors.size > 0) {
				for (const stillValidProductUrl of stillValidUrls) {
					if (violationUrlsOfProductsDueToColors.has(stillValidProductUrl)) {
						finalViolationUrlsOfProductDueToColor.add(stillValidProductUrl);
					}
				}
			}
		}
	} catch (ex) {
		console.error(ex);
	}

	// update the the information in the store
	const { pageStore } = rootStore;
	const { presentationWizardStore } = pageStore as PresentationPageStore;
	const { setFutureViolationColorUrls, setFutureViolationUrlsOfProduct, setFutureViolationUrlsOfProductDueToColor } = presentationWizardStore;

	runInAction(() => {
		setFutureViolationUrlsOfProduct(finalViolationUrlsOfProduct);
		setFutureViolationUrlsOfProductDueToColor(finalViolationUrlsOfProductDueToColor);
		setFutureViolationColorUrls(finalViolationUrlsOfColor);
	});
}

/** return specs for keys stored in custom view */
export function getSpecsFromCustomView(cv: C8Node, matrixPath?: ExtendedPath): Spec[] {
	const obj = cv.Keys as Record<string, string>;
	const keys = Object.values(obj).map((dpkey) => new Key({ dpkey }));
	return getSpecsForKeys(keys, matrixPath);
}

/** return detailed specs for given type */
export function getSpecsForType(nodeType: string): CardSpecs {
	return csiCard.getAttributeCardDetailedSpecs(nodeType, true);
}

/** return attribute specs to use for given product */
export function getProductAttributeSpecs(product: C8Node): Spec[] {
	return getSpecsForType(product.$Type).attributeSpecs;
}

/** return store to edit set */
export async function getSetEditStore(presentationUrl: cnl, setUrl?: cnl, cache: ICache = new WorkingCache()): Promise<SetEditStore> {
	if (setUrl) {
		const paths = [
			pathCreate(SET_ITEMS, SET_ITEM_PRODUCT, DEFAULT_IMAGE_PATH),
			pathCreate(SET_ITEMS, SET_ITEM_COLOR, DEFAULT_IMAGE_PATH),
			pathCreate(SET_ITEMS, SET_ITEM_COLOR, COLOR_SPECIFICATION),
		];

		await resolveNodesWithDependencies([setUrl], paths, cache);
	}

	return new SetEditStore(presentationUrl, setUrl, cache);
}

/**
 * TO DO - might need to remove 
 * get all the sets under this presentation which this product is in,
 * Note: this function rely on set items, so need to make sure that the PPSetItems are cached
 */
export async function getSetsProductUsedIn2(presentationUrl: cnl, product: C8Node, cache: ICache): Promise<C8Node[]> {
	let ppSetItems = cache.getNodes(product.PPSetItems);

	// the set items could come from other presentations, then won't be part of cache, so clean it up here
	ppSetItems = _.compact(ppSetItems);

	const ppSetUrls = ppSetItems.map((setItem) => setItem.__Parent__);
	const setNodes = await resolveNodes(ppSetUrls, cache);
	const usedInSetList = _.uniq(setNodes).filter((set) => set.__Parent__ === presentationUrl);

	return usedInSetList;
}

/**
 * query the server toget all the sets under this presentation which this product is in
 */
export async function getSetsProductUsedIn(presentationUrl: cnl, productUrl: cnl): Promise<NameAndUrl[]> {
	const search = new Search({ Operation: "NamesByXML" });

	search.getQuery()
		.Node(xml.PROP_TYPE, xml.OP_EQUALS, PRODUCT_PRESENTATION_SET)
		.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, presentationUrl)
		.RefAttr(SET_ITEM_PRODUCT, xml.OP_EQUALS, productUrl, pathCreate(SET_ITEMS));

	const response = await search.execute();

	return response.getNameAndUrls();
}

/** return paths for specs used for filtering color matrix */
export function getMatrixFilterPaths(filterSpecs: Spec[], inMatrix?: boolean, pathPrefix?: string): string[] {
	const paths: string[] = [];

	if (filterSpecs) {
		for (const spec of filterSpecs) {
			const { matrixExtendedPath, querySpec: { path, pathInMatrix } } = spec;

			if (matrixExtendedPath) {
				const standardPath = convertToStandardPath(inMatrix ? pathInMatrix : path);
				paths.push(pathCreate(pathPrefix, standardPath));
			}
		}
	}

	return paths;
}

/** dependency paths required to display products */
export function getProductAttributePaths(prefix?: string): string[] {
	const paths = [];

	if (prefix) {
		paths.push(prefix);
	}

	if (hasModule(FOOD_MODULE)) {
		csiCard.getPathsForSpecs(getSpecsForType(FOOD), prefix, paths);
	}

	csiCard.getPathsForSpecs(getSpecsForType(MATERIAL), prefix, paths);
	csiCard.getPathsForSpecs(getSpecsForType(STYLE), prefix, paths);
	return paths;
}

/** dependency paths required to display product gallery, to support both attribute display and filtered display of colors */
export function getProductDependencyPaths(showAttributes: boolean, filterSpecs?: Spec[], includeSetItems?: boolean): string[] {
	const attributePaths = showAttributes ? getProductAttributePaths() : [];
	const filterPaths = getMatrixFilterPaths(filterSpecs);

	return [
		pathCreate("ProductColors", COLOR_SPECIFICATION),
		includeSetItems ? pathCreate(BASEPRODUCT_PRESENTATION_SET_ITEMS) : null,
		...attributePaths,
		...filterPaths,
	];
}

/** return dependency paths required for Set items; just assume they could be of any product type */
export function getSetItemDependencyPaths(filterSpecs?: Spec[]): string[] {
	const attributePaths = getProductAttributePaths(pathCreate(SET_ITEM_PRODUCT));
	const filterPaths = getMatrixFilterPaths(filterSpecs, true, SET_ITEM_COLOR);

	return [
		pathCreate(SET_ITEM_COLOR, DEFAULT_IMAGE_PATH),
		pathCreate(SET_ITEM_COLOR, COLOR_SPECIFICATION),
		...attributePaths,
		...filterPaths,
	];
}

/** determine number of sets product is referenced by in the presentation */
export async function getProductUseCountInPresentation(product: C8Node, presentationUrl: cnl): Promise<number> {
	if (!product.PPSetItems.length) {
		return 0;
	}

	const search = getSearch("ProductPresentationSet.BaseProduct", product.$URL, { presentationUrl }).useCountOperation();
	const response = await runSearch(search);
	return response.getTotalCount();
}

/** get the urls of sets with violations under a particular presentation */
export function setHasViolationUnderPresentation(presentation: C8Node, set: C8Node, runtimeRules: RuntimeRulesSchema, cache: ICache): boolean {
	const colorMatrixPath = getPresentationColorMatrixPath(presentation);
	const productQuery = getRuntimeRulesQuery(runtimeRules, null, onApplyPresentationProductFilter);
	const colorQuery = getRuntimeRulesQuery(runtimeRules, colorMatrixPath);

	// collect all the products and sets within these sets
	const productUrls: Set<cnl> = new Set();
	const colorUrls: Set<cnl> = new Set();

	cache.getNodes(set.Items).forEach((item) => {
		const { PPIProduct, PPIProductColor } = item;
		productUrls.add(PPIProduct);

		if (xml.isValidURL(PPIProductColor)) {
			colorUrls.add(PPIProductColor);
		}
	});

	const products = cache.getNodes(Array.from(productUrls.values()));
	const colors = cache.getNodes(Array.from(colorUrls.values()));

	// calculate the validity of product and color
	const stillValidProductNodes = productQuery ? queryProcessor.applyQueryToNodes(productQuery, products, cache) : null;

	if (stillValidProductNodes?.length !== products.length) {
		return true;
	}

	const stillValidColors = colorQuery ? queryProcessor.applyQueryToNodes(colorQuery, colors, cache) : null;

	return stillValidColors?.length !== colors.length;
}

/** apply extra filter to ProductColors to allow for products that do not support color */
export const onApplyPresentationProductFilter: RuleOnApplyFilterFunction = (valueQuery, spec) => {
	if (spec.matrixExtendedPath) {
		valueQuery?.Attr("HasColor", xml.OP_EQUALS, false, pathCreate("ProductType", "ProductionConfig"));
	}
};

/** update adHoc list for primary type and secondary type */
export function setAdHocIds(primary: [string, string[]], secondary: [string, string[]]): void {
	const { productPresentation } = localStore;
	const {
		setAdHocIdsList,
	} = productPresentation;

	setAdHocIdsList(primary[0], primary[1]);
	setAdHocIdsList(secondary[0], secondary[1]);
}